import { useCallback, useMemo, useState } from 'react';

import { TreeTableRow } from './types';

export interface TreeExpansion {
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Opens every collapsible row. */
  expandAll: () => void;
  /** Closes every collapsible row. */
  collapseAll: () => void;
  /** True when every collapsible row is open. */
  allExpanded: boolean;
}

/** Ids of the rows that have children and a toggle: `alwaysExpanded` rows are skipped. */
export const collapsibleIds = <T>(rows: TreeTableRow<T>[]): string[] =>
  rows.flatMap((row) => {
    const hasChildren = (row.children?.length ?? 0) > 0;
    const own = hasChildren && !row.alwaysExpanded ? [row.id] : [];
    return [...own, ...collapsibleIds(row.children ?? [])];
  });

/** The default expansion state for a TreeTable. Callers may own the state instead and pass their own props. */
export const useTreeExpansion = <T>(rows: TreeTableRow<T>[], defaultExpandedIds: string[] = []): TreeExpansion => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(defaultExpandedIds));
  const parentIds = useMemo(() => collapsibleIds(rows), [rows]);

  const isExpanded = useCallback((id: string) => expandedIds.has(id), [expandedIds]);

  const toggle = useCallback((id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setExpandedIds(new Set(parentIds)), [parentIds]);
  const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

  const allExpanded = useMemo(
    () => parentIds.length > 0 && parentIds.every((id) => expandedIds.has(id)),
    [parentIds, expandedIds],
  );

  return { isExpanded, toggle, expandAll, collapseAll, allExpanded };
};
